import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { deriveIntakeProgress } from '../../intakeProgress';
import type { IntakeHubProviderRosterEntry, IntakePracticeVerification } from '../../types';
import { IntakePill } from '../shell/IntakePill';
import { footnoteSx, overlineSx, tableHeadSx, wf } from '../shell/tokens';

const STATUS_TONES: Record<string, 'green' | 'amber' | 'slate'> = {
  Verified: 'green',
  'Awaiting verification': 'amber',
};

const rowGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1.4fr) 170px 220px' },
  gap: '14px',
  alignItems: 'center',
};

/** Review remains in the staff form, which owns submission checks and verify/discard mutations. */
export function AdminIntakeVerification({
  providers,
  selectedUserId,
  rosterEntries,
  verification,
  onReview,
}: {
  providers: { id: string; label: string }[];
  selectedUserId: string;
  rosterEntries?: IntakeHubProviderRosterEntry[];
  verification?: IntakePracticeVerification | null;
  onReview: (userId: string) => void;
}) {
  const visible = selectedUserId ? providers.filter(item => item.id === selectedUserId) : providers;
  return (
    <Stack spacing={2}>
      <Box>
        <Typography sx={overlineSx}>Step 3 · Staff review</Typography>
        <Typography
          variant="h5"
          sx={{ fontSize: '18px', fontWeight: 700, color: wf.ink, mt: '4px' }}
        >
          JotPsych verification
        </Typography>
        <Typography sx={{ fontSize: '13px', color: wf.muted, lineHeight: 1.6, mt: '5px' }}>
          Review each provider’s submitted intake. Confirm or discard verification from their
          intake review; verification is recorded separately for each provider.
        </Typography>
      </Box>
      {verification && (
        <Alert severity={verification.state === 'verified' ? 'success' : 'info'}>
          {verification.provider_counts.verified} of {verification.provider_counts.total} roster
          providers verified
          {' · '}
          {verification.provider_counts.awaiting} awaiting verification
          {' · '}
          {verification.provider_counts.incomplete} incomplete
        </Alert>
      )}
      {visible.length === 0 ? (
        <Alert severity="info">No practice members are available to review.</Alert>
      ) : (
        <Box
          sx={{
            border: `1px solid ${wf.line}`,
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: wf.surface,
          }}
        >
          <Box
            sx={{
              ...rowGrid,
              ...tableHeadSx,
              padding: '8px 20px',
              display: { xs: 'none', sm: 'grid' },
            }}
          >
            <Box>Provider</Box>
            <Box>Intake</Box>
            <Box>Verification</Box>
          </Box>
          {visible.map(provider => {
            const entry = rosterEntries?.find(row => row.user_id === provider.id);
            const progress = deriveIntakeProgress(entry?.progress_inputs);
            const status = !rosterEntries
              ? 'Status unavailable'
              : !entry
                ? 'Not in intake roster'
                : entry.journey_status?.verified_at
                  ? 'Verified'
                  : entry.journey_status?.submitted_at
                    ? 'Awaiting verification'
                    : entry.journeys_pending
                      ? 'Intake setup pending'
                      : 'Intake incomplete';
            return (
              <Box
                key={provider.id}
                sx={{ ...rowGrid, padding: '12px 20px', borderBottom: `1px solid #f3f3f7` }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
                    {provider.label}
                  </Typography>
                  {progress && (
                    <Typography sx={{ fontSize: '12px', color: wf.meta, mt: '2px' }}>
                      Intake {progress.percentComplete}% complete
                    </Typography>
                  )}
                </Box>
                <Box>
                  <IntakePill tone={STATUS_TONES[status] ?? 'slate'} label={status} />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => onReview(provider.id)}
                    sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 700 }}
                  >
                    Review intake for {provider.label}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      <Typography sx={footnoteSx}>
        Agreements and EHR migration apply to the organization. Payer connections and credentialing
        records are available in Go Live.
      </Typography>
    </Stack>
  );
}
